import { graphql } from "gatsby";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";

import { useLabels } from "../../../contexts/labels";
import { useDataLayer } from "../../../hooks/use-data-layer";
import { usePageFullPath } from "../../../hooks/use-page-full-path";
import { FilterContentGroup, GoogleAnalyticsEvent } from "../../../utils/analytics";
import { getSearchParams, getUpdatedSearchParamsUrl } from "../../../utils/search-params";
import { JobsGrid, useGetJobs } from "../../jobs/jobs-grid";
import { getJobTitle } from "../../jobs/utils";
import { JobsFiltersForm, useJobsFiltersForm } from "./use-jobs-filters-form";

export interface JobsGridSectionProps extends Queries.JobsGridSectionDataFragment {}

export const JobsGridSection: FC<JobsGridSectionProps> = ({ detailPage, node_locale }) => {
  const { pushEvent } = useDataLayer();
  const searchParams = getSearchParams();
  const page = parseInt(searchParams?.get("page") ?? "0");

  const defaultValues: JobsFiltersForm = {
    location: searchParams?.get("location") ?? "",
    position: searchParams?.get("position") ?? "",
    macrofunction: searchParams?.get("macrofunction") ?? "",
  };

  const filtersForm = useJobsFiltersForm({
    defaultValues,
  });
  const { handleSubmit } = filtersForm;
  const [appliedFilters, setAppliedFilters] = useState<JobsFiltersForm>(defaultValues);
  const { jobs, loading, error } = useGetJobs({ autoFirstFetch: true });

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0);
    }
  }, [page]);

  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      let match = true;
      const { position, location, macrofunction } = appliedFilters;
      if (position) {
        match = match && Boolean(getJobTitle(job).toLowerCase().includes(position.toLowerCase()));
      }
      if (location) {
        match = match && job.country === location;
      }
      if (macrofunction) {
        match = match && job.macrofunction === macrofunction;
      }
      return match;
    });
  }, [appliedFilters, jobs]);

  const { labels } = useLabels();

  const detailPageFullPath = usePageFullPath(detailPage?.id);
  const getLinkToJobDetailPage = useCallback(
    (id: string) => `${detailPageFullPath}?jobId=${id}`,
    [detailPageFullPath]
  );

  const onSubmit = useCallback(
    (values: JobsFiltersForm) => {
      setAppliedFilters(values);

      pushEvent({
        event: GoogleAnalyticsEvent.Filter,
        content_group: FilterContentGroup.JobList,
      });

      if (typeof window !== "undefined") {
        const path = getUpdatedSearchParamsUrl(values);
        window.history.pushState({ path }, "", path);
      }
    },
    [pushEvent]
  );

  return (
    <FormProvider {...filtersForm}>
      <JobsGrid
        allJobs={jobs}
        filteredJobs={filteredJobs}
        loading={loading}
        error={error}
        labels={labels}
        node_locale={node_locale}
        detailPage={detailPage}
        getLinkToJobDetailPage={getLinkToJobDetailPage}
        onFilter={handleSubmit(onSubmit)}
      />
    </FormProvider>
  );
};

export const query = graphql`
  fragment JobsGridSectionData on ContentfulJobsGridSection {
    node_locale
    detailPage {
      id
    }
  }
`;
